import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    productCategory: '',
    projectStage: '',
    message: '',
    timeline: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send form data to backend
      const response = await axios.post(`${API}/consultation`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        productCategory: formData.productCategory,
        projectStage: formData.projectStage,
        timeline: formData.timeline,
        message: formData.message
      });

      if (response.data) {
        toast({
          title: "Consultation Request Received!",
          description: "Our R&D team will contact you within 24 hours.",
        });

        // Clear form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          productCategory: '',
          projectStage: '',
          message: '',
          timeline: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your request. Please try again or call us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-secondary">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="inline-block mb-6">
              <span className="text-sm font-semibold text-brand-teal uppercase tracking-wider">
                Get In Touch
              </span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-light leading-tight mb-6">
              Let's Develop Your <span className="font-medium text-brand-teal">Next Innovation</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Share your product vision with us. Our R&D team will review your requirements and 
              schedule a consultation to discuss how we can bring your formulation to life.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-medium mb-8">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-teal/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="text-brand-teal" size={20} />
                  </div>
                  <div>
                    <p className="font-medium mb-1">Phone</p>
                    <a href="tel:+918121712626" className="text-muted-foreground hover:text-brand-teal">
                      +91 812 171 2626
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-teal/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="text-brand-teal" size={20} />
                  </div>
                  <div>
                    <p className="font-medium mb-1">Email</p>
                    <a href="mailto:info@vcosmeticcrafters.com" className="text-muted-foreground hover:text-brand-teal">
                      info@vcosmeticcrafters.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-teal/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-brand-teal" size={20} />
                  </div>
                  <div>
                    <p className="font-medium mb-1">Location</p>
                    <p className="text-muted-foreground">
                      SR Towers, IDA Jeedimetla<br />
                      Hyderabad, Telangana - 500055<br />
                      India
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 bg-secondary">
                <p className="text-sm font-semibold mb-2">Business Hours</p>
                <p className="text-sm text-muted-foreground">
                  Monday - Saturday: 9:00 AM - 6:00 PM IST<br />
                  Sunday: Closed
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-input bg-background focus:outline-none focus:ring-2 focus:ring-brand-teal"
                      placeholder="Your name" />

                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-input bg-background focus:outline-none focus:ring-2 focus:ring-brand-teal"
                      placeholder="your@email.com" />

                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-input bg-background focus:outline-none focus:ring-2 focus:ring-brand-teal"
                      placeholder="+91 XXXXX XXXXX" />

                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium mb-2">
                      Company/Brand Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-input bg-background focus:outline-none focus:ring-2 focus:ring-brand-teal"
                      placeholder="Your brand" />

                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="productCategory" className="block text-sm font-medium mb-2">
                      Product Category *
                    </label>
                    <select
                      id="productCategory"
                      name="productCategory"
                      required
                      value={formData.productCategory}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-input bg-background focus:outline-none focus:ring-2 focus:ring-brand-teal">

                      <option value="">Select category</option>
                      <option value="skincare">Skincare</option>
                      <option value="haircare">Haircare</option>
                      <option value="personal-care">Personal Care</option>
                      <option value="cosmeceuticals">Cosmeceuticals</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="projectStage" className="block text-sm font-medium mb-2">
                      Project Stage *
                    </label>
                    <select
                      id="projectStage"
                      name="projectStage"
                      required
                      value={formData.projectStage}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-input bg-background focus:outline-none focus:ring-2 focus:ring-brand-teal">

                      <option value="">Select stage</option>
                      <option value="idea">Just an idea</option>
                      <option value="research">Market research phase</option>
                      <option value="formulation">Need formulation development</option>
                      <option value="testing">Testing & validation</option>
                      <option value="scale-up">Ready for scale-up</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="timeline" className="block text-sm font-medium mb-2">
                    Desired Timeline
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-input bg-background focus:outline-none focus:ring-2 focus:ring-brand-teal">

                    <option value="">Select timeline</option>
                    <option value="urgent">Urgent (Within 1 month)</option>
                    <option value="1-3-months">1-3 months</option>
                    <option value="3-6-months">3-6 months</option>
                    <option value="flexible">Flexible</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-2">
                    Project Details *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className="w-full px-4 py-3 border border-input bg-background focus:outline-none focus:ring-2 focus:ring-brand-teal resize-none"
                    placeholder="Tell us about your product vision, target market, key requirements, or any specific questions...">
                  </textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center px-8 py-4 text-sm font-semibold text-white bg-brand-teal hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed">

                  {isSubmitting ? 'Sending...' : 'Request Consultation'}
                  <Send size={18} className="ml-2" />
                </button>

                <p className="text-sm text-muted-foreground">
                  * Required fields. We respect your privacy and will never share your information.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map or Additional CTA */}
      <section className="py-24 bg-secondary">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-light mb-6">
              Prefer a Direct Conversation?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Call us directly to speak with our R&D team. We're here to answer your questions 
              and provide immediate guidance on your formulation needs.
            </p>
            <a
              href="tel:+918121712626"
              className="inline-flex items-center justify-center px-8 py-4 text-sm font-semibold text-white bg-brand-teal hover:opacity-90"
            >
              <Phone size={18} className="mr-2" />
              Call +91 812 171 2626
            </a>
          </div>
        </div>
      </section>
    </div>);

};

export default Contact;